type SnowEntry = {
  snow_type: string | null;
  from_altitude: number | null;
  to_altitude: number | null;
  draw_middle_layer?: boolean;
  expos: any;
};

type AvalancheEntry = {
  problem: string | null;
  from_altitude: number | null;
  to_altitude: number | null;
  draw_middle_layer?: boolean;
  expos: any;
};

type ReportData = {
  snow: SnowEntry[];
  avalanche: AvalancheEntry[];
  snowData?: Record<string, any>;
  avalancheData?: Record<string, any>;
  [key: string]: any;
};

export function mergeExpos(existingExpos, newExpos) {
  const existingSet = new Set(existingExpos);
  const newSet = new Set(newExpos);
  return Array.from(new Set([...existingSet, ...newSet]));
}

export function processReportData(reportData: ReportData): ReportData {
  // Filtere ungültige Einträge heraus
  reportData.snow = reportData.snow.filter((entry) => entry.snow_type !== null);
  reportData.avalanche = reportData.avalanche.filter(
    (entry) => entry.problem !== null
  );

  // === SNOW DATA ===
  reportData.snowData = {};
  for (const sid in reportData.snow) {
    const snowEntry = reportData.snow[sid];
    const snowType = snowEntry.snow_type as string;
    const fromAltitude =
      snowEntry.from_altitude === 0 ? null : snowEntry.from_altitude;
    const toAltitude =
      snowEntry.to_altitude === 0 ? null : snowEntry.to_altitude;
    const drawMiddleLayer = !!snowEntry.draw_middle_layer;
    let expos = snowEntry.expos;

    // Skip falls Höhenbereich unsinnig
    if (
      fromAltitude !== null &&
      toAltitude !== null &&
      fromAltitude >= toAltitude
    )
      continue;

    if (!(snowType in reportData.snowData)) {
      reportData.snowData[snowType] = new Map<string, any[]>();
    }

    const snowTypeMap = reportData.snowData[snowType] as Map<string, any[]>;
    const key = `${fromAltitude}-${toAltitude}`;

    if (snowTypeMap.has(key)) {
      const existingEntry = snowTypeMap.get(key)!;
      const existingExpos = existingEntry[2];
      expos = mergeExpos(existingExpos, expos);
      snowTypeMap.set(key, [
        fromAltitude,
        toAltitude,
        expos,
        drawMiddleLayer || existingEntry[3],
      ]);
    } else {
      snowTypeMap.set(key, [fromAltitude, toAltitude, expos, drawMiddleLayer]);
    }
  }

  // Konvertiere Maps zu Arrays
  for (const snowType in reportData.snowData) {
    const map = reportData.snowData[snowType] as Map<string, any[]>;
    reportData.snowData[snowType] = Array.from(map.values());
  }

  // === AVALANCHE DATA ===
  reportData.avalancheData = {};
  for (const aid in reportData.avalanche) {
    const avalancheEntry = reportData.avalanche[aid];
    const avalancheType = avalancheEntry.problem as string;
    const fromAltitude =
      avalancheEntry.from_altitude === 0 ? null : avalancheEntry.from_altitude;
    const toAltitude =
      avalancheEntry.to_altitude === 0 ? null : avalancheEntry.to_altitude;
    const drawMiddleLayer = !!avalancheEntry.draw_middle_layer;
    let expos = avalancheEntry.expos;

    if (
      fromAltitude !== null &&
      toAltitude !== null &&
      fromAltitude >= toAltitude
    )
      continue;

    if (!(avalancheType in reportData.avalancheData)) {
      reportData.avalancheData[avalancheType] = new Map<string, any[]>();
    }

    const avalancheTypeMap = reportData.avalancheData[avalancheType] as Map<
      string,
      any[]
    >;
    const key = `${fromAltitude}-${toAltitude}`;

    if (avalancheTypeMap.has(key)) {
      const existingEntry = avalancheTypeMap.get(key)!;
      const existingExpos = existingEntry[2];
      expos = mergeExpos(existingExpos, expos);
      avalancheTypeMap.set(key, [
        fromAltitude,
        toAltitude,
        expos,
        drawMiddleLayer || existingEntry[3],
      ]);
    } else {
      avalancheTypeMap.set(key, [
        fromAltitude,
        toAltitude,
        expos,
        drawMiddleLayer,
      ]);
    }
  }

  // Konvertiere Maps zu Arrays
  for (const avalancheType in reportData.avalancheData) {
    const map = reportData.avalancheData[avalancheType] as Map<string, any[]>;
    reportData.avalancheData[avalancheType] = Array.from(map.values());
  }

  return reportData;
}
